import { useEffect, useState } from "react";
import { DeviceMobile, X } from "@phosphor-icons/react";

/**
 * PWA install prompt — captures the `beforeinstallprompt` event and surfaces
 * a discreet floating banner. Dismissal is remembered in localStorage so we
 * never nag the same user twice. iOS doesn't fire the event (Safari blocks
 * programmatic installs), so on iOS we show a one-time tooltip explaining
 * "Share → Add to Home Screen".
 *
 * Renders in the global layout via App.js — same level as <CookieConsent>.
 */
const DISMISS_KEY = "hc_pwa_install_dismissed";
const DISMISS_TTL_DAYS = 30;

function dismissedRecently() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const at = parseInt(raw, 10);
        if (!at) return false;
        return Date.now() - at < DISMISS_TTL_DAYS * 24 * 3600 * 1000;
    } catch { return false; }
}

function markDismissed() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator?.standalone === true;
}

function isIos() {
    if (typeof navigator === "undefined") return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent)
        && !/CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);
}

export default function PwaInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if (isStandalone() || dismissedRecently()) return;
        // Chrome/Edge/Opera/Samsung
        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", handler);

        // Safari iOS: no event fires, so we surface a manual hint after a short delay.
        if (isIos()) {
            const t = setTimeout(() => { setIosHint(true); setShow(true); }, 12000);
            return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
        }
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice?.outcome === "accepted") {
                setShow(false);
            }
        } catch (_e) { /* user declined or browser blocked */ }
        setDeferred(null);
        markDismissed();
    };

    const dismiss = () => { setShow(false); markDismissed(); };

    if (!show) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-40 bg-[#0b0f19]/95 border border-cyan-500/40 rounded-sm shadow-2xl backdrop-blur-sm p-4"
            data-testid="pwa-install-prompt"
        >
            <div className="flex items-start gap-3">
                <div className="bg-cyan-400/10 border border-cyan-400/30 rounded-sm p-2 shrink-0">
                    <DeviceMobile size={18} weight="duotone" className="text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-mono mb-1">// install app</div>
                    {iosHint ? (
                        <p className="text-xs text-slate-300 leading-relaxed">
                            Tap <span className="text-cyan-300 font-mono">Share</span> in Safari,
                            then <span className="text-cyan-300 font-mono">Add to Home Screen</span> to
                            install hacking.community.
                        </p>
                    ) : (
                        <p className="text-xs text-slate-300 leading-relaxed">
                            Install hacking.community for fast access, offline reading, and
                            push notifications. No app store needed.
                        </p>
                    )}
                    {!iosHint && deferred && (
                        <button
                            onClick={install}
                            data-testid="pwa-install-btn"
                            className="mt-3 bg-cyan-400 hover:bg-cyan-300 text-slate-950 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-sm transition-colors"
                        >
                            install
                        </button>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss install prompt"
                    data-testid="pwa-install-dismiss"
                    className="text-slate-500 hover:text-slate-300 transition-colors"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
